import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Key, Eye, EyeOff } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { supabase } from '../../lib/supabase';

interface PasswordResetModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    id: string;
    email: string;
  } | null;
}

const PasswordResetModal: React.FC<PasswordResetModalProps> = ({
  isOpen,
  onClose,
  user
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      setError('No se ha seleccionado un usuario');
      return;
    }
    
    // Validate password
    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    
    // Validate password match
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Update password using hybrid RPC function (compatible with existing users)
      const { data, error } = await supabase.rpc('update_user_password_hybrid', {
        user_id: user.id,
        new_password: password
      });
      
      if (error || !data?.success) {
        throw new Error(data?.message || 'Error updating password');
      }
      
      if (error) {
        throw new Error(`Error al actualizar la contraseña: ${error.message}`);
      }
      
      setSuccess(true);
      
      // Reset form
      setPassword('');
      setConfirmPassword('');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
      
    } catch (error: any) {
      // Handle different types of errors
      let errorMessage = 'Error al actualizar la contraseña';
      
      if (error.message) {
        if (error.message.includes('Database error')) {
          errorMessage = 'Error de base de datos. Verifique la configuración de Supabase.';
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Error de conexión. Verifique su conexión a internet.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Generate a random password
  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()';
    let result = '';
    for (let i = 0; i < 12; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(result);
    setConfirmPassword(result);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[500px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white dark:bg-gray-800 p-6 shadow-lg">
          <Dialog.Title className="text-xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
            <Key className="mr-2 h-5 w-5 text-gray-700 dark:text-gray-300" />
            Cambiar Contraseña
          </Dialog.Title>
          
          {success ? (
            <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-md">
              <p className="text-green-800 dark:text-green-200 font-medium">Contraseña actualizada correctamente</p>
              <p className="text-green-700 dark:text-green-300 text-sm mt-1">
                La contraseña del usuario ha sido actualizada con éxito.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-md mb-4">
                  <p className="text-blue-800 dark:text-blue-200 font-medium">Cambiar contraseña para:</p>
                  <p className="text-blue-700 dark:text-blue-300 text-sm mt-1">
                    {user?.email || 'Usuario seleccionado'}
                  </p>
                </div>
                
                <div>
                  <div className="relative">
                    <Input
                      label="Nueva contraseña"
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-9 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    La contraseña debe tener al menos 8 caracteres
                  </p>
                </div>
                
                <div className="relative">
                  <Input
                    label="Confirmar contraseña"
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-9 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={generatePassword}
                  >
                    Generar Contraseña
                  </Button>
                </div>
              </div>
              
              {error && (
                <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md text-sm">
                  {error}
                </div>
              )}
              
              <div className="mt-6 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading || !password || !confirmPassword}
                >
                  {isLoading ? 'Actualizando...' : 'Actualizar Contraseña'}
                </Button>
              </div>
            </form>
          )}
          
          <Dialog.Close asChild>
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default PasswordResetModal;